import { Edit } from "@mui/icons-material";
import { IconButton, Typography } from "@mui/material";
import { Column, PrescriptionWithMedication } from "src/api/types";
import { theme } from "src/assets/theme";

export const useColumns = (
  onEdit: (v: {
    id: number;
    productId: number;
    patientId: number;
    count?: number;
    medication_id?: number;
  }) => void,
): Column<PrescriptionWithMedication>[] => {
  return [
    {
      id: "edit",
      label: "Edit",
      minWidth: 80,
      align: "center",
      orderable: false,
      format: (row) => row.id.toString(),
      render: (row) => (
        <IconButton
          onClick={() =>
            onEdit({
              id: row.id,
              productId: row.product_id,
              patientId: row.patient_id,
              count: row.medications?.length,
              medication_id: row.medications?.[0].medication.id,
            })
          }
        >
          <Edit sx={{ color: theme.palette.secondary.main }} />
        </IconButton>
      ),
    },
    {
      id: "patient_id",
      label: "Patient ID",
      minWidth: 50,
      align: "center",
      orderable: false,
      format: (row) => row.patient_id.toString(),
      render: (row) => <Typography>{row.patient_id}</Typography>,
    },
    {
      id: "medication_name",
      label: "Medication",
      minWidth: 90,
      align: "center",
      orderable: false,
      format: (row) => row.medications?.[0].medication.name ?? "N/A",
      render: (row) => {
        const medication = row.medications?.[0]?.medication;

        if (medication == null) {
          return <Typography>N/A</Typography>;
        }

        return <Typography>{`${medication.name}`}</Typography>;
      },
    },
    {
      id: "medication_dosage",
      label: "Medication dosage",
      minWidth: 90,
      align: "center",
      orderable: false,
      format: (row) => row.medications?.[0].medication.id.toString() ?? "N/A",
      render: (row) => {
        const medication = row.medications?.[0]?.medication;

        if (medication == null) {
          return <Typography>N/A</Typography>;
        }

        return (
          <Typography>{`${medication.dosage} ${medication.dispense_unit}`}</Typography>
        );
      },
    },
    {
      id: "form",
      label: "Form",
      minWidth: 90,
      align: "center",
      orderable: false,
      format: () => "form",
      render: (row) => {
        const medication = row.medications?.[0]?.medication;

        if (medication == null) {
          return <Typography>N/A</Typography>;
        }
        return <Typography>{medication.form}</Typography>;
      },
    },
    {
      id: "quantity",
      label: "Quantity",
      minWidth: 90,
      align: "center",
      orderable: false,
      format: (row) => row.id.toString(),
      render: (row) => <Typography>{row.medications?.length ?? 0}</Typography>,
    },
    {
      id: "total",
      label: "Total",
      minWidth: 90,
      align: "center",
      orderable: false,
      format: () => "total",
      render: (row) => {
        const { medications = [] } = row;

        if (medications[0]?.medication.form === "injectable") {
          const total = medications.reduce(
            (acc, item) => (acc += parseFloat(item.medication.dosage)),
            0,
          );

          const { medication } = medications[0];

          const unit = medication.dosage.replace(/\d+(\.\d+)?/, "").trim();

          return (
            <Typography>{`${total} ${unit} ${medication.dispense_unit}`}</Typography>
          );
        }

        return <Typography>N/A</Typography>;
      },
    },
    {
      id: "pharmacy",
      label: "Pharmacy",
      minWidth: 60,
      align: "center",
      orderable: false,
      format: (row) => row.pharmacy?.name ?? "N/A",
      render: (row) => {
        if (row.pharmacy == null) {
          return <Typography>N/A</Typography>;
        }

        return <Typography>{`${row.pharmacy.name}`}</Typography>;
      },
    },
  ];
};
